/**
 * WizardStepDealSelect - Step 0 do wizard de contrato (modo standalone)
 * 
 * Permite selecionar cliente/contato e negócio antes de gerar contrato.
 * Reutiliza ContactSelector, DealSelector, CustomerFormDialog e DealFormDialog.
 * 
 * @version 2.0.0
 * RELEASE: 2026-04-09 v2.0.0 - Adicionado ContactSelector + botões criação inline
 */
import { useState } from 'react';
import { Briefcase, Info, Plus, User } from 'lucide-react';
import { ContactSelector } from '@/components/crm/ContactSelector';
import { DealSelector } from '@/components/tasks/DealSelector';
import { CustomerFormDialog } from '@/components/crm/customers/CustomerFormDialog';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface WizardStepDealSelectProps {
  /** ID do deal selecionado */
  value: string | null;
  /** Callback ao selecionar um deal */
  onSelect: (dealId: string | null) => void;
  /** Callback com dados completos do deal (contact_id) */
  onDealData?: (deal: { id: string; contact_id: string | null } | null) => void;
  /** ID do contato selecionado (gerenciado pelo wizard pai) */
  contactId: string | null;
  /** Callback ao selecionar contato */
  onContactSelect: (contactId: string | null) => void;
}

/**
 * Componente do Step 0 — Seleção de cliente/contato e negócio
 * Reutiliza seletores e modais existentes (zero duplicação de código)
 */
export function WizardStepDealSelect({
  value,
  onSelect,
  onDealData,
  contactId,
  onContactSelect,
}: WizardStepDealSelectProps) {
  // Estado dos modais de criação inline
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [createDealOpen, setCreateDealOpen] = useState(false);

  /**
   * Callback ao criar cliente via modal inline
   * Pré-seleciona o contato principal do cliente recém-criado
   */
  const handleCustomerCreated = (newCustomer?: { id: string; main_contact_id: string }) => {
    if (newCustomer?.main_contact_id) {
      onContactSelect(newCustomer.main_contact_id);
    }
    setCreateCustomerOpen(false);
  };

  /**
   * Callback ao selecionar cliente existente (duplicata detectada)
   * Pré-seleciona o contato principal do cliente selecionado
   */
  const handleSelectExistingCustomer = (customer: { main_contact_id: string | null }) => {
    if (customer.main_contact_id) {
      onContactSelect(customer.main_contact_id);
    }
    setCreateCustomerOpen(false);
  };

  /**
   * Callback ao criar negócio via modal inline
   * Pré-seleciona o deal recém-criado
   */
  const handleDealCreated = (newDeal?: { id: string }) => {
    if (newDeal?.id) {
      onSelect(newDeal.id);
    }
    setCreateDealOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Orientação ao usuário */}
      <Alert variant="default" className="border-primary/20 bg-primary/5">
        <Info className="h-4 w-4 text-primary" />
        <AlertDescription className="text-sm text-muted-foreground">
          Selecione o cliente/contato e o negócio para o qual deseja gerar um contrato.
          Os dados serão utilizados para preencher automaticamente as variáveis do contrato.
        </AlertDescription>
      </Alert>

      {/* Campo: Cliente / Contato */}
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          Cliente / Contato
        </label>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <ContactSelector
              value={contactId}
              onSelect={(id) => {
                onContactSelect(id);
                // Limpa negócio ao trocar contato (negócios serão re-filtrados)
                if (id !== contactId) {
                  onSelect(null);
                }
              }}
              placeholder="Buscar cliente ou contato..."
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={() => setCreateCustomerOpen(true)}
            title="Criar novo cliente"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Busque pelo nome, telefone ou e-mail do cliente/contato.
        </p>
      </div>

      {/* Campo: Negócio */}
      <div className="space-y-2">
        <label className="text-sm font-medium flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-muted-foreground" />
          Negócio
        </label>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <DealSelector
              value={value}
              onSelect={onSelect}
              contactId={contactId}
              onDealData={onDealData}
              placeholder="Buscar e selecionar negócio..."
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={() => setCreateDealOpen(true)}
            title="Criar novo negócio"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Busque pelo título do negócio. {contactId ? 'Exibindo negócios do contato selecionado.' : 'Selecione um contato para filtrar.'}
        </p>
      </div>

      {/* Modal de criação de cliente inline */}
      <CustomerFormDialog
        customer={null}
        open={createCustomerOpen}
        onOpenChange={setCreateCustomerOpen}
        onSuccess={handleCustomerCreated}
        onSelectExistingCustomer={handleSelectExistingCustomer}
      />

      {/* Modal de criação de negócio inline (pré-preenche contato selecionado) */}
      <DealFormDialog
        open={createDealOpen}
        onOpenChange={setCreateDealOpen}
        /* Seleção inline do wizard: sem modal de sucesso */
        showSuccessDialog={false}
        defaultContactId={contactId || undefined}
        onSuccess={handleDealCreated}
      />
    </div>
  );
}
